import { useEffect, useState } from "react";
import { Trophy, Swords } from "lucide-react";
import { useLiveRoomEvents } from "@/hooks/useLiveRoomWS";
import UserBadge from "@/components/UserBadge";

function fmtSecs(s) {
  if (s <= 0) return "0:00";
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
}

/**
 * Slim battle overlay banner at the top of a live stream.
 * Shows both hosts, their coin scores, and remaining time.
 *
 * Phase 4B — subscribes to `battle:score` and `battle:end` frames on the
 * room WS so both audiences see the scoreboard tick in real time
 * without polling. Falls back to whatever the parent passed in `battle`
 * if the WS is unavailable.
 */
export default function BattleBanner({ battle: parentBattle, viewerStreamId }) {
  const [battle, setBattle] = useState(parentBattle);
  const [remaining, setRemaining] = useState(0);

  // Keep local state in sync with parent hydration.
  useEffect(() => { setBattle(parentBattle); }, [parentBattle]);

  // Phase 4B — real-time score + end updates.
  useLiveRoomEvents((msg) => {
      if (!msg) return;
      if (msg.channel === "battle:score" && msg.battle_id === battle?.id) {
        setBattle((b) => (b ? {
          ...b,
          host_score: msg.host_score,
          opponent_score: msg.opponent_score,
        } : b));
      }
      if (msg.channel === "battle:end" && msg.battle_id === battle?.id) {
        setBattle((b) => (b ? {
          ...b,
          status: "ended",
          winner_id: msg.winner_id,
          host_score: msg.host_score,
          opponent_score: msg.opponent_score,
        } : b));
      }
  }, { enabled: !!viewerStreamId });

  useEffect(() => {
    if (!battle?.ends_at || battle.status !== "active") {
      setRemaining(0);
      return;
    }
    const tick = () => {
      const secs = Math.max(0, Math.floor((new Date(battle.ends_at).getTime() - Date.now()) / 1000));
      setRemaining(secs);
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [battle?.ends_at, battle?.status]);

  if (!battle) return null;

  const hostSide = battle.host_score;
  const oppSide = battle.opponent_score;
  const total = Math.max(1, hostSide + oppSide);
  const hostPct = Math.round((hostSide / total) * 100);
  const oppPct = 100 - hostPct;

  const isEnded = battle.status === "ended";
  const winnerId = battle.winner_id;
  const isTie = isEnded && hostSide === oppSide;
  const iAmOnHostSide = battle.host_stream_id === viewerStreamId;

  return (
    <div
      data-testid="battle-banner"
      className="w-full bg-gradient-to-r from-rose-950/85 via-black/85 to-purple-950/85 backdrop-blur-md border-b border-rose-500/30"
    >
      {/* Iter69 — slim single-row Battle bar. Roughly half the height of
          the previous 3-row layout so it stops covering performers'
          faces. Layout: Host A identity+score on the left, timer + VS
          badge in the center, Host B score+identity on the right. A
          1-px score progress line replaces the fat progress bar. */}
      <div className="flex items-center justify-between gap-2 px-3 py-1.5 text-[11px]">
        {/* Host A side */}
        <div className="flex items-center gap-1.5 min-w-0 flex-1">
          <UserBadge
            userRef={battle.host?.id || battle.host?.handle}
            className={`truncate ${iAmOnHostSide ? "text-amber-400 font-semibold" : "text-zinc-200"} focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-400 rounded-sm`}
            data-testid="battle-banner-host-handle"
          >
            {battle.host?.display_name}
            {isEnded && winnerId === battle.host?.id && !isTie && (
              <Trophy size={11} className="inline-block ml-0.5 -mt-0.5 text-amber-400" />
            )}
          </UserBadge>
          <span
            className="font-mono font-semibold text-rose-300 tabular-nums"
            data-testid="battle-banner-host-score"
          >
            {hostSide.toLocaleString()}
          </span>
        </div>

        {/* Center: timer + status */}
        <div className="flex items-center gap-1.5 shrink-0 px-1">
          <span
            className="font-mono font-semibold text-white tabular-nums"
            data-testid="battle-banner-timer"
          >
            {isEnded ? "0:00" : fmtSecs(remaining)}
          </span>
          <div className="flex items-center justify-center w-5 h-5 rounded-full bg-gradient-to-br from-rose-600 to-purple-700 border border-white/20">
            {isEnded ? <Trophy size={11} className="text-white" /> : <Swords size={11} className="text-white" />}
          </div>
        </div>

        {/* Host B side */}
        <div className="flex items-center gap-1.5 min-w-0 flex-1 justify-end">
          <span
            className="font-mono font-semibold text-purple-300 tabular-nums"
            data-testid="battle-banner-opp-score"
          >
            {oppSide.toLocaleString()}
          </span>
          <UserBadge
            userRef={battle.opponent?.id || battle.opponent?.handle}
            className={`truncate ${!iAmOnHostSide ? "text-amber-400 font-semibold" : "text-zinc-200"} focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-400 rounded-sm`}
            data-testid="battle-banner-opp-handle"
          >
            {isEnded && winnerId === battle.opponent?.id && !isTie && (
              <Trophy size={11} className="inline-block mr-0.5 -mt-0.5 text-amber-400" />
            )}
            {battle.opponent?.display_name}
          </UserBadge>
        </div>
      </div>

      {/* 1-px lead indicator — same at-a-glance signal the old fat bar
          gave, but doesn't add height to the banner. */}
      <div className="h-px w-full flex bg-black/60" data-testid="battle-banner-score-line">
        <div className="bg-rose-500 transition-all duration-500" style={{ width: `${hostPct}%` }} />
        <div className="bg-purple-500 transition-all duration-500" style={{ width: `${oppPct}%` }} />
      </div>

      {/* Ended/tie state — one thin line only when needed, no extra
          space during the live battle. */}
      {isEnded && (
        <div
          className="text-center text-[9px] uppercase tracking-widest text-rose-300/80 px-2 py-0.5"
          data-testid="battle-banner-ended-label"
        >
          {isTie ? "Battle tied" : "Battle ended"}
        </div>
      )}
    </div>
  );
}
